<script lang="ts">
  type Trend = 'up' | 'down' | 'flat' | null;

  interface Props {
    label: string;
    value: string | number;
    unit?: string;
    delta?: string | null;
    trend?: Trend;
    eyebrow?: string;
    href?: string | null;
  }

  let {
    label,
    value,
    unit = '',
    delta = null,
    trend = null,
    eyebrow,
    href = null,
  }: Props = $props();

  const Tag = $derived(href ? 'a' : 'div');
</script>

<svelte:element
  this={Tag}
  href={href ?? undefined}
  class="kpi"
  class:clickable={!!href}
>
  {#if eyebrow}<div class="eyebrow">{eyebrow}</div>{/if}
  <div class="label">{label}</div>
  <div class="value-row">
    <span class="value">{value}</span>
    {#if unit}<span class="unit">{unit}</span>{/if}
  </div>
  {#if delta}
    <div class="delta" class:up={trend === 'up'} class:down={trend === 'down'} class:flat={trend === 'flat'}>
      {#if trend === 'up'}▲{:else if trend === 'down'}▼{:else if trend === 'flat'}—{/if}
      <span>{delta}</span>
    </div>
  {/if}
</svelte:element>

<style>
  .kpi {
    display: block;
    background: var(--color-white);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: var(--space-lg) var(--space-xl);
    box-shadow: var(--shadow-card);
    text-decoration: none;
    color: var(--color-text);
    transition: box-shadow var(--duration-base) var(--ease-standard);
  }
  .kpi.clickable:hover {
    box-shadow: var(--shadow-card-hover);
    cursor: pointer;
  }
  .eyebrow {
    font-family: var(--font-ui);
    font-size: var(--text-xs);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-wider);
    text-transform: uppercase;
    color: var(--color-blue);
    margin-bottom: var(--space-xs);
  }
  .label {
    font-family: var(--font-ui);
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    margin-bottom: var(--space-sm);
  }
  .value-row {
    display: flex;
    align-items: baseline;
    gap: var(--space-xs);
  }
  .value {
    font-family: var(--font-display);
    font-size: var(--text-3xl);
    font-weight: var(--weight-bold);
    line-height: var(--leading-tight);
    color: var(--color-navy);
    letter-spacing: var(--tracking-tight);
    font-variant-numeric: tabular-nums;
  }
  .unit {
    font-family: var(--font-ui);
    font-size: var(--text-md);
    color: var(--color-text-muted);
    font-weight: var(--weight-medium);
  }
  .delta {
    margin-top: var(--space-sm);
    font-family: var(--font-ui);
    font-size: var(--text-sm);
    font-weight: var(--weight-semibold);
    display: inline-flex;
    align-items: center;
    gap: var(--space-xs);
  }
  .delta.up   { color: var(--color-positive); }
  .delta.down { color: var(--color-negative); }
  .delta.flat { color: var(--color-text-muted); }
</style>
